import axios from 'axios';
import _, { Dictionary } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useSWR from 'swr';
import { Box, Grid, IconButton, Stack, Typography } from '@mui/material';
import { HubConnection } from '@microsoft/signalr';
import {
  selectSavedTextMessageForContact,
  setSavedTextMessageForContact,
} from '@store/reducers/contactManagerSlice';
import { RootState } from '@store/store';
import { formatDate, initiateWSConnection, withQueryParams } from '@shared/commonFunctions';
import { SmsSearch } from '@shared/components/Layout/components/Header/components/SmsSearchInput';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { SERVICES } from '@shared/constants/apiConfig';
import { countryDateFormat, timeFormat } from '@shared/constants/formats';
import { ArrowUpwardIcon, BoxWrapper, Dateinfo } from '@styles/ContactManager/HeaderStyles';
import { colorTheme, extraColor } from '@styles/muiTheme';
import { StyleCommentchat, StyledChatInput, StyledChatMessage } from '@styles/StyledChatBox';
import { useGetConsentQuery } from '@modules/ContactManager/components/Tabs/components/Contact/queries';
import { IContactChatModalProps, IMessages, ISMSHistory, ISmsSend } from './interfaces';
import { requestSendSms } from './requests';

const smsStatusRed = ['failed', 'undelivered', 'partially_delivered', 'canceled'];
const PHOENIX_SENDER_TYPE ='Phoenix';
const PHOENIX_SMS_REASON ='A message is sent to customer';


const renderAllMessage = (data: IMessages, prevTime: string) => {
  const outboundApi = 'outbound-api';
  const idMatch: boolean = data.direction === outboundApi;
  const currentTime = formatDate(data.createDate, { format: timeFormat });
  const sender = data.senderName || '';
  const caption =
    prevTime != currentTime ? `${sender} ${data.fromNumber} ${currentTime}` : `${sender} ${data.fromNumber} ${prevTime}`;
  const bgColor = smsStatusRed.includes(data.smsStatus)
    ? extraColor.palette.error.primary
    : data.smsStatus === 'received'
    ? extraColor.palette.grey.offwhite
    : extraColor.palette.blue['500'];

  return (
    <Box
     key={`${idMatch ? 'mine' : 'their'}-${sender}-${formatDate(data.createDate, { format: 'x' })}-${data.message}-${data.id}`}
     className={idMatch ? 'main-chat-wrapper-reply' : 'main-chat-wrapper-talking'}
    >
      <Box>
        <Box>
          <Box className="chat-time chat-small-label">
            {idMatch ? (
              <Typography
                variant="caption"
                display="flex"
                justifyContent="right"
              >
                {caption}
              </Typography>
            ) : (
              <Typography variant="caption">{caption}</Typography>
            )}
          </Box>
        </Box>
        <StyledChatMessage
          className={idMatch ? 'reply-bubble tri-right right-top' : 'talk-bubble tri-right left-top'}
          bgColor={bgColor}
        >
          <Box className="talktext">{data.message}</Box>
          <Box className="talktext">
            {data?.medias?.map(({ mediaContentType, mediaUrl }) => {
              if (mediaContentType.indexOf('image') > -1) {
                return (
                  <Box
                    key={data.id}
                    component="img"
                    src={mediaUrl}
                    alt={data?.message}
                    maxWidth={350}
                  />
                );
              } else if (mediaContentType.indexOf('pdf') > -1) {
                return (
                  <Box
                    key={data.id}
                    component="object"
                    type="application/pdf"
                    data={mediaUrl}
                    maxWidth={350}
                    height={400}
                  />
                );
              }
              return null;
            })}
          </Box>
          <Typography
            variant="caption"
            color={data.smsStatus === 'received' ? colorTheme.palette.common.black : colorTheme.palette.common.white}
            display="flex"
            justifyContent="right"
          >
            {data.smsStatus}
          </Typography>
        </StyledChatMessage>
      </Box>
    </Box>
  );
};

const renderTimeDaySeparator = (createdDate: string): JSX.Element => {
  return (
    <Dateinfo
      key={`date-${createdDate}`}
      variant="subtitle1"
    >
      {formatDate(createdDate)}
    </Dateinfo>
  );
};

const groupMessagesByDate = (data: IMessages[]): Dictionary<IMessages[]> => {
  return _.groupBy(data, (value) => moment(value.createDate).format(countryDateFormat));
};

const getPhoneWithIsdCode = (phone: string) => {
  return `+1${phone}`;
};

export const ContactChatModal = ({ contactNumber, contactId, contactDetails }: IContactChatModalProps) => {
  const dispatch = useDispatch();
  const savedMessage = useSelector((state: RootState) => selectSavedTextMessageForContact(state, contactId));
  const [textMessage, setTextMessage] = useState(!savedMessage ? '' : savedMessage);
  const [filteredData, setFilteredData] = useState<Dictionary<IMessages[]>>({});
  const [smsMessages, setMessages] = useState<IMessages[]>([]);
  const [disable, setDisable] = useState(false);
  const [connection, setConnection] = useState<HubConnection>();

  const ref = useRef<HTMLDivElement>();

  const handleScroll = useCallback(() => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: 'auto' });
    }
  }, [ref]);

  const { data: smsHistoryData } = useSWR<ISMSHistory>(
    contactNumber &&
      withQueryParams(`${SERVICES.CONTACT}/phone-chats/sms/history/by-contact-id`, {
        id: contactId,
        phoneNumber: getPhoneWithIsdCode(contactNumber),
      }, false),
  );
  const { consentData } = useGetConsentQuery(contactDetails);

  const smsHistoryArray = useMemo(() => smsHistoryData || { phoneNumber: '', textOptOut: false, messages: [] } as ISMSHistory, [smsHistoryData]);
  
const onReceive = useCallback(
  (message: IMessages) => {
    // The contact name is not being displayed when receiving a message from the contact.
    // This is used to populate that name because it takes the modal being closed and reopened in order to see that name.
    if (!message.senderName) {
      const existingMessage = smsMessages.find((msg) => msg.fromNumber === message.fromNumber);
      if (existingMessage) {
        message.senderName = existingMessage.senderName;
      }
    }


    const existingIndex = smsMessages.findIndex((msg) => msg.id === message.id);

    let messages: IMessages[];
    if (existingIndex !== -1) {
      messages = [...smsMessages];
      messages[existingIndex] = { ...messages[existingIndex], ...message };
    } else {
      messages = smsMessages.concat(message);
    }

    messages.sort((a, b) => new Date(a.createDate).getTime() - new Date(b.createDate).getTime());

    setFilteredData(groupMessagesByDate(messages));
    setMessages(messages);
  },
  [smsMessages]
);

  useEffect(() => {
    const url = `${axios.defaults.baseURL}/${SERVICES.CONTACT}/chat`;
    const initiateAndStartConnection = async () => {
      try {
        // Initiate the WebSocket connection
        const newConnection = await initiateWSConnection(url);

        // Start the connection
        await newConnection.start();

        // Store the connection in the component's state
        setConnection(newConnection);
      } catch (error) {
        console.error('Connection failed: ', error);
      }
    };
    
		// Call the function to initiate and start the connection
    initiateAndStartConnection();

    return () => {
      const connState = 'Connected';
      if (connection && connection.state === connState) {
        setTimeout(() => {
          connection.stop();
        }, 1000);
      }
    };
  }, []);

  useEffect(() => {
    if (connection && connection.state === 'Connected') {
      connection.on('Receive', onReceive);
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      connection.invoke('SubscribeByPhoneNumber', getPhoneWithIsdCode(contactNumber));
    }

    return () => {
      if (connection && connection.state === 'Connected') {
        connection.off('Receive');
        connection.invoke('UnsubscribeFromPhoneNumber', getPhoneWithIsdCode(contactNumber));
      }
    };
  }, [connection, connection?.state, onReceive]);

  useEffect(() => {
    if (smsHistoryArray?.messages) {
      const sortedMessages = smsHistoryArray.messages.sort(
        (a, b) => new Date(a.createDate).getTime() - new Date(b.createDate).getTime()
      );

      setFilteredData(groupMessagesByDate(sortedMessages));
      setMessages(sortedMessages || []);
    }
    setDisable(smsHistoryArray?.textOptOut ?? false);
  }, [smsHistoryArray]);

  useEffect(() => {
    const checkApplicationSMSonsent = consentData?.find((consent) => consent?.consentPreferenceName === 'Application_SMS');
    setDisable(!checkApplicationSMSonsent?.isOptedIn);
  }, [consentData]);

  useEffect(() => {
    handleScroll();
  }, [smsMessages]);

  const renderMessages = useCallback((): JSX.Element[] => {
    const res: JSX.Element[] = [];
    const msgs = Object.keys(filteredData);

    msgs.forEach((createDate: string) => {
      const dayMessages = filteredData[createDate];
      let prevTime = formatDate(new Date().getTime().toString(), { format: timeFormat });
      res.push(renderTimeDaySeparator(createDate));
      dayMessages.forEach((c) => {
        const message = renderAllMessage(c, prevTime);
        res.push(message);
        prevTime = formatDate(c.createDate, { format: timeFormat });
      });
    });

    return res;
  }, [filteredData]);

  const handleOnSearch = useCallback(
    (value: string) => {
      setFilteredData(
        groupMessagesByDate(smsMessages.filter((message) => message.message.toLowerCase().includes(value.toLowerCase())) ?? [])
      );
    },
    [smsMessages]
  );

  const handleSendMessage = useCallback(async () => {
    if (textMessage) {
  
      const sendJson: ISmsSend = {
        contactId: contactId,
        phoneNumber: getPhoneWithIsdCode(contactNumber),
        fromNumber: '',
        body: textMessage,
        reason: PHOENIX_SMS_REASON,
        senderType: PHOENIX_SENDER_TYPE,
      };

      setDisable(true);
      const resp = await requestSendSms(sendJson);
      setDisable(false);

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (resp.status === 200) {
        setTextMessage('');
        dispatch(setSavedTextMessageForContact({ 
          contactId, 
          message: null 
        }));
      }
    } else {
      Notistack.generalError('Please type a message to send!!');
    }
  }, [textMessage]);

  const onEnter = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        handleSendMessage();
      }
    },
    [handleSendMessage]
  );

  const handleMessageChange = useCallback(
    (message: string) => {
      setTextMessage(message);
      dispatch(setSavedTextMessageForContact({ 
        contactId, 
        message 
      }));
    },[dispatch]
  );

  return (
    <>
      <StyleCommentchat />
      <Stack
        pt={3}
        spacing={2}
      >
        <SmsSearch
          isWithDeleteIcon
          placeholder="Search"
          onSearch={handleOnSearch}
        />

        {disable && (
          <Box
            display="flex"
            justifyContent="center"
            color={colorTheme.palette.error.main}
          >
            Contact is opted out from texts.
          </Box>
        )}

        <BoxWrapper>
          <Box sx={{ overflow: 'auto' }}>
            {renderMessages()}
            <Box ref={ref} />
          </Box>
          <Grid
            container
            pt={2}
            spacing={1}
          >
            <Grid
              item
              xs={11}
            >
              <StyledChatInput
                maxRows={6}
                placeholder="Type a message"
                value={textMessage}
                disabled={disable}
                onChange={({ target: { value } }) => handleMessageChange(value)}
                onKeyDown={onEnter as unknown as React.KeyboardEventHandler<HTMLTextAreaElement>}
              />
            </Grid>

            <Grid
              item
              xs={1}
              alignSelf="self-end"
            >
              <IconButton
                disabled={disable}
                sx={{ padding: '0px', marginBottom: '5px' }}
                onClick={handleSendMessage}
              >
                <ArrowUpwardIcon disable={!textMessage} />
              </IconButton>
            </Grid>
          </Grid>
        </BoxWrapper>
      </Stack>
    </>
  );
};
